import { makeObservable, override } from 'mobx';

import AppSettings from '../../../app/AppSettings';
import { ForceDownloadUrlBuilder } from '../../../app/configurator/ForceDownloadUrlBuilder';
import { IApiClient } from '../../../app/data/client';
import { GetWebShopProductsQuery, StoreContext, StoreData, WebShopProductListData } from '../../../app/data/model';
import { GetWebShopCmsContentQuery, WebShopPageData } from '../../../app/data/model';
import { HomePageTranslation } from '../../../app/localization/SiteTranslation';
import { BasePageState } from '../../../app/shared/BasePageState';
import type { PageMetadata } from '../../../app/shared/common';
import { GalleryState } from '../../../app/shared/components/Gallery/GalleryState';
import { IEventAggregator } from '../../../app/shared/EventAggregator';
import { INavigationService } from '../../../app/shared/NavigationService';
import { StoreState } from '../../../app/StoreState';
import logoImage from '../../../images/logoWithBackground.jpg';
import ProductThumbnailState from './components/ProductList/ProductThumbnail/ProductThumbnailState';
import { WebShopProductListViewedEvent, WebShopProductSelectedEvent } from './HomePageEvents';

export interface IHomePageMemento {
  pageContent: WebShopPageData;
  products: WebShopProductListData[];
  storeContext: StoreContext;
}

export class HomePageState extends BasePageState<IHomePageMemento> {
  public readonly gallery: GalleryState;
  public pageContent?: WebShopPageData;
  public products: WebShopProductListData[] = [];
  public storeContext?: StoreContext;

  public productThumbnails: ProductThumbnailState[] = [];

  public constructor(
    settings: AppSettings,
    private readonly client: IApiClient,
    public readonly translation: HomePageTranslation,
    private readonly navigation: INavigationService,
    private readonly eventAggregator: IEventAggregator,
  ) {
    super();

    makeObservable(this);

    const forceDownloadBuilder = new ForceDownloadUrlBuilder(settings.functionsUrl);
    this.gallery = new GalleryState(forceDownloadBuilder, 800, 600);

    this.onPageLoaded = this.onPageLoaded.bind(this);
    this.navigation.pageLoaded.subscribe(this.onPageLoaded);

    this.notifyProductSelected = this.notifyProductSelected.bind(this);
  }

  @override
  get metadata(): PageMetadata {
    return {
      title: this.translation.pageTitleFormat,
      description: this.translation.pageDescriptionFormat,
      imageUrl: logoImage,
    };
  }

  public async onLoad(store: StoreState) {
    const webShopCmsContentResponse = await this.client.send(
      new GetWebShopCmsContentQuery({ shoppingContext: store.shoppingContext }),
    );

    const webShopProductsResponse = await this.client.send(
      new GetWebShopProductsQuery({ shoppingContext: store.shoppingContext }),
    );

    this.initialize(
      webShopCmsContentResponse.pageContent,
      webShopProductsResponse.products,
      store.storeResponse.storeContext,
    );
  }

  public getMemento(): IHomePageMemento {
    return {
      pageContent: this.pageContent,
      products: this.products,
      storeContext: this.storeContext,
    };
  }

  public restoreMemento(memento: IHomePageMemento) {
    this.initialize(memento.pageContent, memento.products, memento.storeContext);
  }

  public unload() {
    this.navigation.pageLoaded.unsubscribe(this.onPageLoaded);
  }

  private onPageLoaded() {
    if (this.products.length > 0) {
      this.notifyProductListViewed();
    }
  }

  private notifyProductListViewed() {
    this.eventAggregator.publish(new WebShopProductListViewedEvent(this.storeContext, this.products));
  }

  public notifyProductSelected(product: WebShopProductListData, index: number) {
    this.eventAggregator.publish(new WebShopProductSelectedEvent(this.storeContext, product, index));
  }

  private initialize(content: WebShopPageData, products: WebShopProductListData[], storeContext: StoreContext) {
    this.pageContent = content;
    this.storeContext = storeContext;

    if (content?.gallery?.images) {
      this.gallery.setImages(content.gallery.images);
    }

    this.products = products;
    this.productThumbnails = products.map(
      (product) => new ProductThumbnailState(product, this.translation.productList),
    );
  }
}
